import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { getLiquidacionesAuditoriaApi } from '../../../../api/axios';
import { fechaLimite, filtroMedioMes } from '../../../../utilities/fechasFiltro';
import BigSpinner from '../../../General/BigSpinner';
import SafeReactPaginate from '../../../General/SafeReactPaginate';
import '../../shared/liquidacionesModule.css';
import LiquidacionesPageHeader from '../../shared/LiquidacionesPageHeader';
import LiquidacionesFilterCard from '../../shared/LiquidacionesFilterCard';
import LiquidacionesFilterActions from '../../shared/LiquidacionesFilterActions';
import { LiquidacionesEmptyState } from '../../shared/LiquidacionesButtons';
import { LIQUIDACIONES_ROUTES } from '../../shared/liquidacionesRoutes';
import './LiquidacionDetalleTabla.css';
import './AuditoriaLiquidaciones.css';

const fechaFiltro = filtroMedioMes();
const fechaDesdeMinima = fechaLimite();
const DEFAULT_PAGE_SIZE = 10;

const buildInitialFilters = () => ({
  idLiquidacion: '',
  fechaDesde: fechaFiltro.desde,
  fechaHasta: fechaFiltro.hasta,
  tipoTransaccion: '',
});

const toApiDate = (value, isEndOfDay = false) => {
  if (!value) return undefined;
  return `${value}T${isEndOfDay ? '23:59:59' : '00:00:00'}`;
};

const toNumberOrUndefined = (value) => {
  if (value === '' || value == null) return undefined;
  const parsed = Number.parseInt(String(value).trim(), 10);
  return Number.isNaN(parsed) ? undefined : parsed;
};

const toArray = (value) => (Array.isArray(value) ? value : []);

const normalizeItem = (item) => ({
  idLiquidacion: item?.idLiquidacion ?? item?.IdLiquidacion ?? 0,
  fechaInicio: item?.fechaInicio ?? item?.FechaInicio ?? null,
  fechaFin: item?.fechaFin ?? item?.FechaFin ?? null,
  tipoTransaccion: item?.tipoTransaccion ?? item?.TipoTransaccion ?? '-',
  cantidadPercepciones: item?.cantidadPercepciones ?? item?.CantidadPercepciones ?? 0,
  cantidadRetenciones: item?.cantidadRetenciones ?? item?.CantidadRetenciones ?? 0,
  cantidadOrdenes: item?.cantidadOrdenes ?? item?.CantidadOrdenes ?? 0,
  exitoso: item?.exitoso ?? item?.Exitoso ?? false,
});

const normalizeResponse = (raw) => {
  const data = raw?.data ?? raw ?? {};
  return {
    numeroPagina: data?.numeroPagina ?? data?.NumeroPagina ?? 1,
    cantidadPagina: data?.cantidadPagina ?? data?.CantidadPagina ?? DEFAULT_PAGE_SIZE,
    cantidadTotal: data?.cantidadTotal ?? data?.CantidadTotal ?? 0,
    elementos: toArray(data?.elementos ?? data?.Elementos).map(normalizeItem),
  };
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('es-AR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatDuration = (from, to) => {
  if (!from || !to) return '-';
  const start = new Date(from);
  const end = new Date(to);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return '-';

  const diffMs = end.getTime() - start.getTime();
  if (diffMs < 0) return '-';

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
};

const buildRequest = ({ filters, page, pageSize }) => {
  const request = {
    NumeroPagina: page,
    CantidadPagina: pageSize,
  };

  const idLiquidacion = toNumberOrUndefined(filters.idLiquidacion);
  const tipoTransaccion = toNumberOrUndefined(filters.tipoTransaccion);

  if (idLiquidacion != null) request.IdLiquidacion = idLiquidacion;
  if (filters.fechaDesde) request.FechaDesde = toApiDate(filters.fechaDesde, false);
  if (filters.fechaHasta) request.FechaHasta = toApiDate(filters.fechaHasta, true);
  if (tipoTransaccion != null) request.TipoTransaccion = tipoTransaccion;

  return request;
};

function AuditoriaLiquidaciones({ tipoUsuario, idSeller }) {
  const [filters, setFilters] = useState(buildInitialFilters);
  const [searchParams, setSearchParams] = useState(buildInitialFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [response, setResponse] = useState({
    numeroPagina: 1,
    cantidadPagina: DEFAULT_PAGE_SIZE,
    cantidadTotal: 0,
    elementos: [],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getLiquidacionesAuditoriaApi(buildRequest({
        filters: searchParams,
        page: currentPage,
        pageSize,
      }));

      setResponse(normalizeResponse(data));
    } catch (err) {
      setResponse({
        numeroPagina: currentPage,
        cantidadPagina: pageSize,
        cantidadTotal: 0,
        elementos: [],
      });
      setError(err?.response?.data?.mensaje || 'No se pudo cargar la auditoría de liquidaciones.');
    } finally {
      setLoading(false);
    }
  }, [searchParams, currentPage, pageSize]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setCurrentPage(1);
    setSearchParams(filters);
  };

  const handleReset = (event) => {
    event.preventDefault();
    const nextFilters = buildInitialFilters();
    setFilters(nextFilters);
    setSearchParams(nextFilters);
    setCurrentPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
  };

  const paginationPageSize = Number(response.cantidadPagina) > 0
    ? Number(response.cantidadPagina)
    : pageSize;
  const totalPages = Math.max(1, Math.ceil(Number(response.cantidadTotal || 0) / Math.max(1, paginationPageSize)));
  const safeCurrentPage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (safeCurrentPage !== currentPage) {
      setCurrentPage(safeCurrentPage);
    }
  }, [safeCurrentPage, currentPage]);

  const handlePageClick = (data) => {
    const selectedPage = data.selected + 1;
    setCurrentPage(selectedPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const pageMetrics = useMemo(() => {
    const items = response.elementos;
    const exitosas = items.filter((item) => item.exitoso).length;
    const fallidas = items.filter((item) => !item.exitoso).length;

    return {
      exitosas,
      fallidas,
      enPagina: items.length,
    };
  }, [response.elementos]);

  const summaryCards = useMemo(() => [
    { label: 'Transacciones totales', value: response.cantidadTotal || 0 },
    { label: 'En esta página', value: pageMetrics.enPagina },
    { label: 'Exitosas (página)', value: pageMetrics.exitosas },
    {
      label: 'Fallidas (página)',
      value: pageMetrics.fallidas,
      alert: pageMetrics.fallidas > 0,
    },
  ], [response.cantidadTotal, pageMetrics]);

  const rangoDesde = response.cantidadTotal === 0
    ? 0
    : (safeCurrentPage - 1) * paginationPageSize + 1;
  const rangoHasta = response.cantidadTotal === 0
    ? 0
    : Math.min(safeCurrentPage * paginationPageSize, response.cantidadTotal);

  const canViewAuditoria = Number(tipoUsuario) === 8 || Number(idSeller) === 1;

  if (!canViewAuditoria) {
    return <Navigate to={LIQUIDACIONES_ROUTES.liquidaciones} replace />;
  }

  return (
    <div className="container gestion-liquidaciones liquidaciones-modulo liquidaciones-auditoria">
      <LiquidacionesPageHeader
        title="Auditoría de liquidaciones"
        subtitle="Seguimiento de cálculos y confirmaciones realizadas sobre liquidaciones."
        backTo={LIQUIDACIONES_ROUTES.liquidaciones}
        backLabel="Volver a liquidaciones"
        showBack
      />

      <form
        className="form-horizontal form-material gestion-liquidaciones-form"
        onSubmit={handleSubmit}
        onReset={handleReset}
      >
        <LiquidacionesFilterCard
          actions={(
            <LiquidacionesFilterActions
              disabled={loading}
              searchLabel={loading ? 'Buscando...' : 'Buscar'}
            />
          )}
        >
          <div className="row g-3 align-items-end">
            <div className="col-12 col-md-2">
              <p className="gestion-liquidaciones-filtro-label mb-0">N° liquidación</p>
              <input
                type="text"
                className="form-control"
                name="idLiquidacion"
                autoComplete="off"
                inputMode="numeric"
                value={filters.idLiquidacion}
                onChange={handleInputChange}
                placeholder="Buscar por id"
              />
            </div>
            <div className="col-12 col-md-2">
              <p className="gestion-liquidaciones-filtro-label mb-0">Desde</p>
              <input
                type="date"
                className="form-control"
                name="fechaDesde"
                min={fechaDesdeMinima}
                value={filters.fechaDesde}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-12 col-md-2">
              <p className="gestion-liquidaciones-filtro-label mb-0">Hasta</p>
              <input
                type="date"
                className="form-control"
                name="fechaHasta"
                min={filters.fechaDesde || fechaDesdeMinima}
                value={filters.fechaHasta}
                onChange={handleInputChange}
              />
            </div>
            <div className="col-12 col-md-2">
              <p className="gestion-liquidaciones-filtro-label mb-0">Tipo transacción</p>
              <select
                className="form-control"
                name="tipoTransaccion"
                value={filters.tipoTransaccion}
                onChange={handleInputChange}
              >
                <option value="">Todos</option>
                <option value="1">Calculada</option>
                <option value="2">Confirmada</option>
              </select>
            </div>
            <div className="col-12 col-md-2">
              <p className="gestion-liquidaciones-filtro-label mb-0">Por página</p>
              <select
                className="form-control"
                value={pageSize}
                onChange={(event) => {
                  setPageSize(Number.parseInt(event.target.value, 10));
                  setCurrentPage(1);
                }}
              >
                <option value="10">10 por página</option>
                <option value="20">20 por página</option>
                <option value="50">50 por página</option>
              </select>
            </div>
          </div>
        </LiquidacionesFilterCard>
      </form>

      <div className="liquidaciones-auditoria__metrics">
        {summaryCards.map((card) => (
          <div
            key={card.label}
            className={[
              'liquidaciones-auditoria__metric',
              card.alert ? 'liquidaciones-auditoria__metric--alert' : '',
            ].join(' ').trim()}
          >
            <strong>{card.value}</strong>
            <span>{card.label}</span>
          </div>
        ))}
      </div>

      {response.cantidadTotal > 0 && (
        <div className="liquidaciones-auditoria__info">
          <span>
            Mostrando <strong>{rangoDesde}-{rangoHasta}</strong> de{' '}
            <strong>{response.cantidadTotal}</strong> transacciones
          </span>
          <span>Página {response.numeroPagina || safeCurrentPage} de {totalPages}</span>
        </div>
      )}

      {error ? (
        <div className="alert alert-danger liquidaciones-auditoria__error" role="alert">
          {error}
        </div>
      ) : null}

      {loading ? (
        <BigSpinner message="Cargando auditoría de liquidaciones..." />
      ) : !error && response.elementos.length === 0 ? (
        <LiquidacionesEmptyState>
          No se encontraron transacciones para los filtros seleccionados.
        </LiquidacionesEmptyState>
      ) : !error ? (
        <>
          <div className="gestion-liquidaciones-resultados liquidacion-detalle-seccion">
            <div className="liquidacion-detalle-tabla-card">
              <div className="liquidacion-detalle-tabla-card__header">
                <h5 className="liquidacion-detalle-tabla-card__title mb-0">Transacciones</h5>
                <span className="liquidacion-detalle-tabla-card__badge">
                  {response.elementos.length} en esta página
                </span>
              </div>
              <div className="liquidacion-detalle-tabla-card__body">
                <div className="table-responsive">
                  <table className="table table-hover liquidacion-detalle-tabla mb-0">
                    <thead>
                      <tr>
                        <th>N° liquidación</th>
                        <th>Tipo</th>
                        <th>Fecha inicio</th>
                        <th>Fecha fin</th>
                        <th>Duración</th>
                        <th>Percepciones</th>
                        <th>Retenciones</th>
                        <th>Órdenes</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {response.elementos.map((item, index) => (
                        <tr
                          key={`${item.idLiquidacion}-${item.fechaInicio || index}`}
                          className={!item.exitoso ? 'table-danger' : 'hover-background-color'}
                        >
                          <td><strong>{item.idLiquidacion}</strong></td>
                          <td>{item.tipoTransaccion}</td>
                          <td>{formatDateTime(item.fechaInicio)}</td>
                          <td>{formatDateTime(item.fechaFin)}</td>
                          <td>{formatDuration(item.fechaInicio, item.fechaFin)}</td>
                          <td>{item.cantidadPercepciones}</td>
                          <td>{item.cantidadRetenciones}</td>
                          <td>{item.cantidadOrdenes}</td>
                          <td>
                            {item.exitoso ? (
                              <span className="gestion-liquidaciones-estado-pill gestion-liquidaciones-estado-pill--liquidada">
                                Exitosa
                              </span>
                            ) : (
                              <span className="gestion-liquidaciones-estado-pill gestion-liquidaciones-estado-pill--error">
                                Fallida
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="gestion-liquidaciones-pagination-wrap">
              <SafeReactPaginate
                previousLabel="Anterior"
                nextLabel="Siguiente"
                breakLabel="..."
                breakClassName="break-me"
                pageCount={totalPages}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={handlePageClick}
                containerClassName="pagination liquidacion-detalle-pagination mb-0"
                subContainerClassName="pages pagination"
                activeClassName="active"
                forcePage={safeCurrentPage - 1}
                disableInitialCallback
              />
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

const mapStateToProps = (state) => ({
  tipoUsuario: state.general.tipoUsuario,
  idSeller: state.general.idSeller,
});

export default connect(mapStateToProps)(AuditoriaLiquidaciones);
